import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Mail, RotateCcw, Save, Eye, Sparkles, Variable, MessageCircle } from "lucide-react";

function VarChip({ name, desc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-neutral-900 text-white text-[11px] font-mono rounded hover:bg-neutral-700 transition-colors"
      title={desc}
      data-testid={`var-${name}`}
    >
      <Variable size={10} />{`{{${name}}}`}
    </button>
  );
}

export default function AdminEmails() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [selectedKey, setSelectedKey] = useState("");
  const [draft, setDraft] = useState({ subject: "", body: "", cta_text: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const selected = templates.find((t) => t.key === selectedKey);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data.templates);
      setVariables(data.variables);
      if (!selectedKey && data.templates.length) {
        setSelectedKey(data.templates[0].key);
      }
    } catch (e) {
      toast.error("Falha ao carregar templates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selected) {
      setDraft({ subject: selected.subject, body: selected.body, cta_text: selected.cta_text || "" });
      setPreviewHtml("");
    }
  }, [selectedKey, selected?.subject, selected?.body]);

  const insertVar = (field, name) => {
    const marker = `{{${name}}}`;
    setDraft((d) => ({ ...d, [field]: (d[field] || "") + marker }));
  };

  const save = async () => {
    if (!draft.body.trim()) {
      toast.error("O corpo não pode ficar vazio");
      return;
    }
    if (selected?.channel !== "whatsapp" && !draft.subject.trim()) {
      toast.error("Assunto não pode ficar vazio");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${selectedKey}`, draft);
      toast.success("Template salvo. Próximos envios já usarão este texto.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/${selectedKey}/reset`);
      toast.success("Restaurado para o padrão");
      await load();
    } catch (e) { toast.error("Erro ao restaurar"); }
    finally { setBusy(false); setResetOpen(false); }
  };

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${selectedKey}/preview`, draft);
      setPreviewHtml(data.html);
    } catch (e) { toast.error("Erro no preview"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Comunicação</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">E-mails automáticos</h1>
      <p className="mt-2 text-sm text-neutral-600 max-w-2xl">Personalize os textos que seus clientes recebem em cada etapa. Use as variáveis (chips com fundo preto) para inserir dados dinâmicos como nome, código da OS e valor.</p>

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        {/* Lista de templates */}
        <aside className="lg:col-span-4 space-y-2">
          {loading && <div className="text-sm text-neutral-500">Carregando…</div>}
          {templates.map((t) => (
            <button
              key={t.key}
              onClick={() => setSelectedKey(t.key)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                selectedKey === t.key
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-200 bg-white hover:border-neutral-400"
              }`}
              data-testid={`tpl-${t.key}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 min-w-0">
                  {t.channel === "whatsapp"
                    ? <MessageCircle size={16} className={`mt-1 shrink-0 ${selectedKey === t.key ? "text-green-300" : "text-green-600"}`} />
                    : <Mail size={16} className="mt-1 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{t.label}</div>
                    <div className={`text-[11px] mt-0.5 truncate ${selectedKey === t.key ? "text-neutral-400" : "text-neutral-500"}`}>{t.trigger}</div>
                  </div>
                </div>
                {t.is_customized && <Sparkles size={12} className={selectedKey === t.key ? "text-amber-300" : "text-amber-500"} title="Personalizado" />}
              </div>
            </button>
          ))}
        </aside>

        {/* Editor */}
        <section className="lg:col-span-8">
          {selected ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Editando</div>
                  <div className="font-heading font-bold text-lg">{selected.label}</div>
                </div>
                <div className="flex gap-2">
                  {selected.is_customized && (
                    <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} disabled={busy} data-testid="tpl-reset">
                      <RotateCcw size={12} className="mr-1.5" /> Restaurar padrão
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={preview} disabled={busy} data-testid="tpl-preview">
                    <Eye size={12} className="mr-1.5" /> Preview
                  </Button>
                  <Button size="sm" onClick={save} disabled={saving} className="bg-neutral-900 hover:bg-neutral-800" data-testid="tpl-save">
                    <Save size={12} className="mr-1.5" /> {saving ? "Salvando…" : "Salvar"}
                  </Button>
                </div>
              </div>

              {/* Variables cheat sheet */}
              <div className="mt-5 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Variáveis disponíveis (clique para inserir no corpo)</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(variables).map(([name, desc]) => (
                    <VarChip key={name} name={name} desc={desc} onClick={() => insertVar("body", name)} />
                  ))}
                </div>
              </div>

              {/* Subject — hidden for WhatsApp templates */}
              {selected.channel !== "whatsapp" && (
                <div className="mt-5">
                  <Label className="text-xs uppercase tracking-widest font-bold">Assunto</Label>
                  <div className="mt-2 flex flex-wrap gap-1.5 mb-2">
                    <span className="text-[10px] text-neutral-500 mr-1">Inserir no assunto:</span>
                    {Object.entries(variables).slice(0, 3).map(([name, desc]) => (
                      <VarChip key={name} name={name} desc={desc} onClick={() => insertVar("subject", name)} />
                    ))}
                  </div>
                  <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="h-11 font-mono text-sm" data-testid="tpl-subject" />
                </div>
              )}

              {/* Body */}
              <div className="mt-5">
                <Label className="text-xs uppercase tracking-widest font-bold">
                  {selected.channel === "whatsapp" ? "Mensagem do WhatsApp" : "Corpo do e-mail"}
                </Label>
                <p className="text-[11px] text-neutral-500 mt-1">
                  {selected.channel === "whatsapp"
                    ? "Texto puro (sem HTML). Aparece já preenchido na conversa do WhatsApp Web/App do técnico."
                    : <>Aceita HTML simples: <code className="bg-neutral-100 px-1 rounded">&lt;b&gt;</code>, <code className="bg-neutral-100 px-1 rounded">&lt;i&gt;</code>, <code className="bg-neutral-100 px-1 rounded">&lt;br&gt;</code>.</>}
                </p>
                <Textarea rows={8} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="mt-2 font-mono text-sm" data-testid="tpl-body" />
              </div>

              {/* CTA — hidden for WhatsApp templates */}
              {selected.channel !== "whatsapp" && (
                <div className="mt-5">
                  <Label className="text-xs uppercase tracking-widest font-bold">Texto do botão (CTA)</Label>
                  <Input value={draft.cta_text} onChange={(e) => setDraft({ ...draft, cta_text: e.target.value })} className="mt-2 h-11" placeholder="Ex: Ver detalhes" data-testid="tpl-cta" />
                </div>
              )}

              {/* Preview */}
              {previewHtml && (
                <div className="mt-6 border-t border-neutral-200 pt-6">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Preview com dados de exemplo</div>
                  <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white" data-testid="tpl-preview-panel">
                    <iframe title="preview" srcDoc={previewHtml} className="w-full min-h-[520px] border-0" sandbox="" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-neutral-500 p-8">Selecione um template para editar.</div>
          )}
        </section>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar texto padrão?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso vai apagar suas personalizações neste template e voltar ao texto original do GTec. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tpl-reset-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={reset} className="bg-red-600 hover:bg-red-700 text-white" data-testid="tpl-reset-confirm">Sim, restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
